import { buildWidgetConfig } from './meterApi';
import { getAggregationUnit } from '../utils/meterGraphs';

/**
 * The request behind one water figure.
 *
 * A chart on the dashboard and the same chart inside the comparison view ask
 * the backend the same question, differing only in the window and the scope
 * they ask it over. Assembling that request in both places is how the two
 * would drift: a filter added to one and not the other reads as a discrepancy
 * in the data rather than as a missing line of code.
 *
 * @param {string} title - Card heading, which the response is labelled with.
 * @param {string} financialYear - Year every request is scoped to.
 * @param {{start: object, end: object}} window - Moment bounds for the query.
 * @param {string|number} locationId - Location filter.
 * @param {string|number} subLocationId - Sub-location filter.
 * @param {string} module - Widget module, e.g. 'Water'.
 * @param {string} category - Widget category.
 * @param {string} subCategory - Widget sub-category.
 * @param {string|Array<string>} kpi - KPI or KPIs to request.
 * @param {Array<string>} meterId - Narrow the request to these meters, for a
 *   KPI several report against.
 * @param {string} stackByField - Field that separates the series, when stacked.
 * @param {string} aggregation - minute, hour, day or month.
 * @returns {object|null} A widget config, or null when there is nothing to ask.
 */
export const buildGraphConfig = ({
  title,
  financialYear,
  window,
  locationId,
  subLocationId,
  module = 'Water',
  category,
  subCategory,
  kpi,
  meterId,
  stackByField = null,
  aggregation = 'day',
}) => {
  if (!financialYear || !window?.start || !window?.end) return null;

  const config = {
    financialYear,
    startDateTime: window.start.toISOString(),
    endDateTime: window.end.toISOString(),
    locationId: locationId?.toString() || '1',
    title,
    module,
    category,
    subCategory,
    kpiType: 'EXTERNAL',
    aggregationPeriodValue: 1,
    aggregationPeriodUnit: getAggregationUnit(aggregation),
    xAxisField: 'displayPeriods',
    chartType: 'line',
  };

  if (subLocationId) config.subLocationId = subLocationId.toString();
  if (kpi) config.kpi = kpi;
  if (meterId?.length) config.meterId = meterId;
  if (stackByField) {
    config.enableStacking = true;
    config.stackByField = stackByField;
  }

  return buildWidgetConfig(config);
};
